import React, { useState, useEffect, Fragment } from 'react';
import styled from 'styled-components';
import slugify from 'slugify';
import moment from 'moment';
import { get, sortBy } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';

//import MUI
import { List, ListItem, ListItemText, ListSubheader, TextField } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Divider from '@mui/material/Divider';

import ArrowDown from 'react-ionicons/lib/IosArrowDown';
import ArrowUp from 'react-ionicons/lib/IosArrowUp';
import ArrowForward from 'react-ionicons/lib/IosArrowForward';
import ArrowBack from 'react-ionicons/lib/MdArrowBack';

//import comps
import Edges from '../components/Edges';

import { Link } from 'react-router-dom';

import { useStore } from '../store';
import { appServices } from '../services';
import * as theme from '../theme';
import ExamList from '../components/ExamList';

const School = (props) => {

    const { school } = useParams();

    let history = useHistory();

    const [
        {
            appState: { actives, schools, departments, instructors, sets, examSchedules }
        },
        dispatch
    ] = useStore() as any;


    if (schools && school) {
        let result = schools.find((o) => slugify(o.schoolName, { replacement: '-', lower: true }) === school);
        if (result === undefined) {
            history.push('/notfound', { state: { errorMessage: `The school ${school} is invalid` } });
        }
    }

    const activeSchool = schools
        ? schools.find(
            (o) => slugify(o.schoolName, { replacement: '-', lower: true }) === school
        )
        : null;

    const [search, setSearch] = useState('');

    //Reset the term if the school has changed
    useEffect(() => {
        //TODO: saved term should be at the school.terms level
        if (activeSchool && actives.term) {
            if (undefined === activeSchool.terms.find((t) => t.termSchoolID === actives.term)) {
                const currentTerm = activeSchool.terms.find((o) =>
                    moment().isBetween(o.startDate, o.endDate, null, '[]')
                );
                if (currentTerm) {
                    dispatch({
                        type: 'ADD_ACTIVES',
                        payload: {
                            type: 'term',
                            value: currentTerm.termSchoolID
                        }
                    });
                }
            }
        }
    }, [activeSchool, actives.term]);

    useEffect(() => {
        // load departments
        if (activeSchool && actives.term) {
            loadDepartments();
        }

        if (activeSchool) {
            const isValidTerm = activeSchool.terms.find((o) => o.termSchoolID === actives.term);
            // find active term by comparing today's date with start and end date of terms
            if (!actives.term || isValidTerm === undefined) {
                const currentTerm = activeSchool.terms.find((o) =>
                    moment().isBetween(o.startDate, o.endDate, null, '[]')
                );
                //find the first upcoming term
                const upcomingTerm = activeSchool.terms.find((t) =>
                    moment().isSameOrBefore(t.startDate)
                );
                const mostRecentTerm = activeSchool.terms.reduce((prev, current) =>
                    +prev.termSchoolID > +current.termSchoolID ? prev : current
                );

                let term = currentTerm || upcomingTerm || mostRecentTerm;

                dispatch({
                    type: 'ADD_ACTIVES',
                    payload: {
                        key: 'term',
                        value: term.termSchoolID
                    }
                });

            }
        }


        // load instructors if active term changes
        if (actives.term && !get(instructors, '[activeSchool.schoolID][activeTerm]')) {
            loadInstructors();
        }

        if (actives.term && !examSchedules[actives.term]) {
            loadExamSchedules();
        }

        if (actives.department && actives.term && !sets[actives.department + actives.term]) {
            loadSets(actives.department);
        }
    }, [activeSchool, actives.term]);

    const loadDepartments = async () => {
        await appServices.getDepartmentsBySchool(
            { schoolID: activeSchool.schoolID, termSchoolID: actives.term },
            dispatch
        );
    };

    const loadInstructors = async () => {
        await appServices.getInstructorsBySchoolAndTerm(
            { schoolID: activeSchool.schoolID, termSchoolID: actives.term },
            dispatch
        );
    };

    const loadExamSchedules = async () => {
        await appServices.getExamSchedules({ termSchoolID: actives.term }, dispatch);
    };

    const loadSets = async (departmentID) => {
        await appServices.getSetsByDepartmentID(
            { departmentID, termSchoolID: actives.term },
            dispatch
        );
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
    };

    const handleFilter = (filter) => {
        const slugifiedFilter = slugify(filter, {
            replacement: '',
            lower: true
        });

        const slugifiedSearch = slugify(search, {
            replacement: '',
            lower: true
        });

        if (slugifiedFilter.includes(slugifiedSearch)) {
            return true;
        }
    };

    const handleTabClick = (tab) => {
        dispatch({
            type: 'ADD_ACTIVES',
            payload: {
                key: 'tab',
                value: tab
            }
        });

        dispatch({
            type: 'SET_TO_NULL_ACTIVES',
            payload: ['instructor', 'set']
        });
    };

    const handleDepartmentClick = async (departmentID) => {
        if (departmentID === actives.department) {
            dispatch({
                type: 'SET_TO_NULL_ACTIVES',
                payload: ['department']
            });
        } else {
            if (!sets[departmentID + actives.term]) {
                await loadSets(departmentID);
            }
            // setActiveDepartment(departmentID);
            dispatch({
                type: 'ADD_ACTIVES',
                payload: {
                    key: 'department',
                    value: departmentID
                }
            });
        }
    };

    /*
     * The Term selection needs to be saved
     */
    const handleTermChange = (termSchoolID) => {
        dispatch({
            type: 'ADD_ACTIVES',
            payload: {
                key: 'term',
                value: termSchoolID
            }
        });

        dispatch({
            type: 'SET_TO_NULL_ACTIVES',
            payload: ['tab', 'department']
        });
    };

    const handleSetClick = (setID) => {
        dispatch({
            type: 'ADD_ACTIVES',
            payload: {
                key: 'set',
                value: setID
            }
        });

        history.push(`/${school}/set/${actives.term}/${setID}`);
        window.scrollTo({ top: 0 });
    };

    const handleInstructorClick = (instructorID) => {
        dispatch({
            type: 'ADD_ACTIVES',
            payload: {
                key: 'instructor',
                value: instructorID
            }
        });

        history.push(`/${school}/instructor/${actives.term}/${instructorID}`);
        window.scrollTo({ top: 0 });
    };

    const resetSelections = () => {
        dispatch({
            type: 'SET_TO_NULL_ACTIVES',
            payload: ['tab', 'set', 'instructor', 'term', 'department']
        });
    };

    const programList =
        activeSchool &&
        actives.term &&
        departments[activeSchool.schoolID] &&
        departments[activeSchool.schoolID].filter((dep) => handleFilter(dep.departmentCode));

    let instructorList =
        activeSchool &&
        actives.term &&
        instructors[activeSchool.schoolID] &&
        instructors[activeSchool.schoolID][actives.term] &&
        instructors[activeSchool.schoolID][actives.term].filter((o) => handleFilter(o.name));

    // Contact List Function - Alphabet List

    //TODO: The instructor list must be sorted in react as well
    if (instructorList && instructorList.length > 0) {
        let instructorName =
            instructorList &&
            instructorList.reduce((r, e) => {
                // get first letter of last name
                const alphabet = e.lastName[0].toUpperCase();

                // if there is no property in accumulator with this letter create it
                if (!r[alphabet]) r[alphabet] = { alphabet, record: [e] };
                // if there is, push current element to children array for that letter
                else r[alphabet].record.push(e);

                return r;
            }, {});
        instructorList = Object.values(instructorName);
    }
    return (
        <>
            <div style={{ padding: '40px 0px' }}>
                <Edges size='md'>
                    <Link to='/'>
                        <ArrowBack style={{ width: 25, height: 25 }} onClick={resetSelections} />
                    </Link>

                    {activeSchool && <Headline>School of {activeSchool.schoolName}</Headline>}

                    <Subline children='Select a Term' />

                    {activeSchool && (
                        <Terms>
                            {sortBy(activeSchool.terms, ['startDate']).map((o) => {
                                return (
                                    <Term
                                        key={o.termSchoolID}
                                        active={(o.termSchoolID === actives.term).toString()}
                                        onClick={() => handleTermChange(o.termSchoolID)}
                                        children={o.label}
                                    />
                                );
                            })}
                            <Term style={{ height: 0, padding: 0, margin: 0 }} />
                            <Term style={{ height: 0, padding: 0, margin: 0 }} />
                        </Terms>
                    )}

                    {actives.term && (
                        <>
                            <Subline children='Select a Timetable' />

                            <Container>
                                <Search
                                    variant='outlined'
                                    value={search}
                                    placeholder='Search by Instructor or Program...'
                                    onChange={handleSearch}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <SearchIcon style={{ color: '#878787' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <StyledList subheader={<li />}>
                                    {/* PROGRAM LIST */}
                                    {programList && programList.length > 0 && (
                                        <li>
                                            <ul>
                                                <StyledListSubheader
                                                    onClick={() =>
                                                        handleTabClick(actives.tab === 1 ? null : 1)
                                                    }
                                                >
                                                    <ListItemText primary='Programs' />
                                                    {actives.tab === 1 ? (
                                                        <ArrowUp color={theme.colors.primary} />
                                                    ) : (
                                                        <ArrowDown color={theme.colors.primary} />
                                                    )}
                                                </StyledListSubheader>
                                                {(actives.tab === 1 || search.length > 0) &&
                                                    programList.map((d) => {
                                                        return (
                                                            <Fragment key={d.departmentID}>
                                                                <StyledListItem
                                                                    onClick={() =>
                                                                        handleDepartmentClick(
                                                                            d.departmentID
                                                                        )
                                                                    }
                                                                    active={(
                                                                        d.departmentID ===
                                                                        actives.department
                                                                    ).toString()}
                                                                >
                                                                    <ListItemText
                                                                        primary={d.departmentCode}
                                                                    />{' '}
                                                                    {d.departmentID ===
                                                                    actives.department ? (
                                                                        <ArrowUp color='red' />
                                                                    ) : (
                                                                        <ArrowDown color='#000' />
                                                                    )}
                                                                </StyledListItem>

                                                                {(d.departmentID ===
                                                                        actives.department || search.length > 0) &&
                                                                    sets[
                                                                    actives.department +
                                                                    actives.term
                                                                        ] && (
                                                                        <>
                                                                            {sets[
                                                                            actives.department +
                                                                            actives.term
                                                                                ].length === 0 ? (
                                                                                <EmptySetWarning
                                                                                    children={
                                                                                        'None of the Sets in this Program have data for this term yet'
                                                                                    }
                                                                                />
                                                                            ) : (
                                                                                <SetList>
                                                                                    {sets[
                                                                                    actives.department +
                                                                                    actives.term
                                                                                        ].map((s) => {
                                                                                        return (
                                                                                            <StyledListItem
                                                                                                key={
                                                                                                    s.setID
                                                                                                }
                                                                                                onClick={() =>
                                                                                                    handleSetClick(s.setID)
                                                                                                }
                                                                                                id={`set${s.setID}`}
                                                                                                active={(
                                                                                                    actives.set === s.setID
                                                                                                ).toString()}
                                                                                            >
                                                                                                <ListItemText
                                                                                                    primary={
                                                                                                        s.setCode
                                                                                                    }
                                                                                                />
                                                                                                <ArrowForward
                                                                                                    color={
                                                                                                        theme
                                                                                                            .colors
                                                                                                            .secondary
                                                                                                    }
                                                                                                />
                                                                                            </StyledListItem>
                                                                                        );
                                                                                    })}
                                                                                </SetList>
                                                                            )}
                                                                        </>
                                                                    )}
                                                            </Fragment>
                                                        );
                                                    })}
                                            </ul>
                                        </li>
                                    )}

                                    <Line />

                                    {/* INSTRUCTORS LIST */}
                                    {instructorList && instructorList.length > 0 && (
                                        <li>
                                            <ul>
                                                <StyledListSubheader
                                                    onClick={() =>
                                                        handleTabClick(actives.tab === 0 ? null : 0)
                                                    }
                                                >
                                                    <ListItemText primary='Instructors' />
                                                    {actives.tab === 0 ? (
                                                        <ArrowUp color={theme.colors.secondary} />
                                                    ) : (
                                                        <ArrowDown color={theme.colors.secondary} />
                                                    )}
                                                </StyledListSubheader>
                                                {(actives.tab === 0 || search.length > 0) &&
                                                    instructorList.length > 0 &&
                                                    instructorList.map((obj, idx) => {
                                                        return (
                                                            <div>
                                                                <AlphabetLetter>
                                                                    {obj.alphabet}
                                                                </AlphabetLetter>
                                                                {obj.record.map(
                                                                    (instructor, idx2) => {
                                                                        return (
                                                                            <StyledListItem
                                                                                key={
                                                                                    instructor.instructorID
                                                                                }
                                                                                id={instructor.instructorID}
                                                                                onClick={() =>
                                                                                    handleInstructorClick(instructor.instructorID)
                                                                                }
                                                                                active={(
                                                                                    actives.instructor === instructor.instructorID
                                                                                ).toString()}
                                                                            >
                                                                                <ListItemText
                                                                                    primary={
                                                                                        instructor.name
                                                                                    }
                                                                                />
                                                                                <ArrowForward color='#FFF' />
                                                                            </StyledListItem>
                                                                        );
                                                                    }
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                            </ul>
                                        </li>
                                    )}
                                    {/* 
                                    {instructorList && instructorList.length > 0 && (
                                        <li>
                                            <ul>
                                                <StyledListSubheader
                                                    onClick={() =>
                                                        setActiveTab(activeTab === 0 ? null : 0)
                                                    }
                                                >
                                                    <ListItemText primary='Instructors' />
                                                    {activeTab === 0 ? (
                                                        <ArrowUp color={theme.colors.secondary} />
                                                    ) : (
                                                        <ArrowDown color={theme.colors.secondary} />
                                                    )}
                                                </StyledListSubheader>
                                                {(activeTab === 0 || search.length > 0) &&
                                                    instructorList.map(o => {
                                                        return (
                                                            <StyledListItem
                                                                key={o.instructorID}
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/${props.school}/instructor/${activeTerm}/${o.instructorID}`
                                                                    )
                                                                }
                                                            >
                                                                <ListItemText primary={o.name} />
                                                                <ArrowForward
                                                                    color={theme.colors.secondary}
                                                                />
                                                            </StyledListItem>
                                                        );
                                                    })}
                                                
                                            </ul>
                                        </li>
                                    )} */}
                                </StyledList>
                            </Container>
                            {activeSchool &&
                                actives.term &&
                                activeSchool.terms.find((o) => o.termSchoolID === actives.term) &&
                                examSchedules[actives.term] &&
                                examSchedules[actives.term].length > 0 && (
                                    <ExamList examSchedules={examSchedules[actives.term]} />
                                )}
                        </>
                    )}
                </Edges>
            </div>
        </>
    );
};

const Headline = styled.h1`
    text-align: left;
    color: ${theme.colors.primary};
    margin-bottom: 1em;
`;

const EmptySetWarning = styled.p`
    margin-left: 15px;
`;

const Subline = styled.h4`
    text-transform: uppercase;
`;

const Terms = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    @media (min-width: 640px) {
        margin-bottom: 40px;
    }
`;

const Term = styled('h4' as any)`
    width: 100%;
    padding: 30px 20px;
    margin: 0 0 20px;
    background: ${(props) => (props.active === 'true' ? '#0E7CCC' : '#fff')};
    color: ${(props) => (props.active === 'true' ? '#fff' : theme.colors.primary)};
    box-shadow: 0 5px 10px 1px rgba(0, 0, 0, 0.05);
    text-align: center;
    transition: ease all 0.2s;
    cursor: pointer;

    @media (min-width: 640px) {
        width: calc(33.3333% - 20px);

        &:hover {
            background: #0e7ccc;
            // background: ${theme.colors.secondary};
            color: #fff;
        }
    }
`;

const Container = styled.div`
    background: #fff;
    box-shadow: 0 5px 10px 1px rgba(0, 0, 0, 0.05);
    margin-bottom: 40px;
    display: flex;
    flex-direction: column;

    @media (min-width: 640px) {
        padding: 20px;
        margin-bottom: 60px;
    }
`;

const Search = styled(TextField)`
    && {
        padding: 10px;
        margin-bottom: 5px;
        .MuiInput-underline:before {
            border-color: ${theme.colors.secondary} !important;
        }
    }
    @media (max-width: 640px) {
        padding: 5px;
        margin-bottom: 10px;
    }
`;

const StyledList = styled(List)`
    && {
        width: 100%;
        position: relative;
        overflow: auto;

        li,
        ul {
            margin: 0;
        }
    }
`;

const StyledListSubheader = styled(ListSubheader)`
    && {
        display: flex;
        align-items: center;
        cursor: pointer;
        padding: 10px 16px;

        span {
            color: ${theme.colors.primary};
            font-weight: bold;
            font-size: 18px;
            cursor: pointer;
        }
    }
`;
const AlphabetLetter = styled.div`
    background-color: #ebf2f9;
    padding: 15px 20px;
    font-weight: 600;
    font-size: 18px;
    margin: 25px 10px 15px 10px;
    @media (min-width: 640px) {
        margin: 25px 0 15px 0;
    }
`;

const StyledListItem = styled(ListItem as any)`
    cursor: pointer;
    transition: ease all 0.2s;

    ${(props) =>
    props.active === 'true' &&
    `
    background: ${theme.colors.secondary};
    color: #fff;
    svg {
    fill: #fff;
    }
  `}

    &:hover {
        background: ${theme.colors.secondary};
        color: #fff;

        svg {
            fill: #fff;
        }
    }
`;

const SetList = styled.div`
    background: #f7f7f7;
    padding: 20px;
`;
const Line = styled(Divider)`
    && {
        margin: 0.5em 0;
    }
`;

export default School;
